import {
  api,
  LOGIN_URL,
  ACCOUNTS_USERS_URL_START,
  ACCOUNTS_USERS_FORGOT_PASSWORD,
  ACCOUNTS_USERS_REPLACE_PASSWORD,
  ACCOUNTS_USERS_URL_END,
  ACCOUNTS_USERS_SET_PASSWORD,
  RESET_PASSWORD,
  REFRESH,
  SET_PROFILE_URL,
  OTP_CHALLENGE_URL,
  LOGIN_TOTP_CHALLENGE_URL,
  LOGIN_SMS_CHALLENGE_URL,
  SETUP_TOTP_MFA_URL,
  VERIFY_SOFTWARE_TOKEN_URL,
  SETUP_SMS_MFA_URL,
  VERIFY_PHONE_NUMBER_URL,
  ACCOUNTS_CUSTOMERS_URL,
  SECURITY_POLICY_SUFFIX,
  RESET_USER_MFA_SUFFIX,
} from '../api';
import {
  LoginParamsTypes,
  ChangePasswordParamsTypes,
  ForgotePasswordParamsTypes,
  ReplacePasswordParamsTypes,
  RefreshAccessTokenTypes,
  SetProfileTypes,
  OtpChallengeParamsTypes,
  LoginTotpChallengeParams,
  LoginSmsChallengeParams,
  VerifySoftwareTokenParams,
  SetupSmsMfaParams,
  VerifyPhoneNumberParams,
  UpdateSecurityPolicyParams,
  UserAccessTypes,
} from './types';

const login = async (data: LoginParamsTypes) => {
  const response = await api({
    method: 'post',
    url: LOGIN_URL,
    data,
  });
  return response;
};

const changePassword = async (data: ChangePasswordParamsTypes) => {
  const response = await api({
    method: 'post',
    url: `${ACCOUNTS_USERS_URL_START}${ACCOUNTS_USERS_SET_PASSWORD}`,
    data,
  });
  return response;
};

const resetPassword = async (customerId: string, userId: string) => {
  const response = await api({
    method: 'post',
    url: `${ACCOUNTS_USERS_URL_START}/${customerId}${ACCOUNTS_USERS_URL_END}/${userId}${RESET_PASSWORD}`,
  });
  return response.data;
};

const forgotPassword = async (data: ForgotePasswordParamsTypes) => {
  const response = await api({
    method: 'post',
    url: `${ACCOUNTS_USERS_URL_START}${ACCOUNTS_USERS_FORGOT_PASSWORD}`,
    data,
  });
  return response;
};

const replacePassword = async (data: ReplacePasswordParamsTypes) => {
  const response = await api({
    method: 'post',
    url: `${ACCOUNTS_USERS_URL_START}${ACCOUNTS_USERS_REPLACE_PASSWORD}`,
    data,
  });
  return response;
};

const refreshAccessToken = async (data: RefreshAccessTokenTypes) => {
  const response = await api({
    method: 'post',
    url: `${ACCOUNTS_USERS_URL_START}${REFRESH}`,
    data,
  });
  return response.data;
};

const setProfile = async (data: SetProfileTypes) => {
  const response = await api({
    method: 'post',
    url: `${SET_PROFILE_URL}`,
    data,
  });
  return response.data;
};

const submitOtpChallenge = async (data: OtpChallengeParamsTypes) => {
  const response = await api({
    method: 'post',
    url: OTP_CHALLENGE_URL,
    data,
  });
  return response;
};

const loginTotpChallenge = async (data: LoginTotpChallengeParams) => {
  const response = await api({
    method: 'post',
    url: LOGIN_TOTP_CHALLENGE_URL,
    data,
  });
  return response;
};

const loginSmsChallenge = async (data: LoginSmsChallengeParams) => {
  const response = await api({
    method: 'post',
    url: LOGIN_SMS_CHALLENGE_URL,
    data,
  });
  return response;
};

const setupTotpMfa = async (accessToken: string) => {
  const response = await api({
    method: 'post',
    url: SETUP_TOTP_MFA_URL,
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data as string;
};

const verifySoftwareToken = async (data: VerifySoftwareTokenParams, accessToken: string) => {
  const response = await api({
    method: 'post',
    url: VERIFY_SOFTWARE_TOKEN_URL,
    data,
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response;
};

const setupSmsMfa = async (data: SetupSmsMfaParams, accessToken: string) => {
  const response = await api({
    method: 'post',
    url: SETUP_SMS_MFA_URL,
    data,
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data as { verificationRequired: boolean };
};

const verifyPhoneNumber = async (data: VerifyPhoneNumberParams, accessToken: string) => {
  const response = await api({
    method: 'post',
    url: VERIFY_PHONE_NUMBER_URL,
    data,
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response;
};

const updateSecurityPolicy = async (customerId: string, data: UpdateSecurityPolicyParams) => {
  const response = await api({
    method: 'patch',
    url: `${ACCOUNTS_CUSTOMERS_URL}/${customerId}${SECURITY_POLICY_SUFFIX}`,
    data,
  });
  return response;
};

const resetUserMfa = async (customerId: string, userId: string) => {
  const response = await api({
    method: 'post',
    url: `${ACCOUNTS_USERS_URL_START}/${customerId}${ACCOUNTS_USERS_URL_END}/${userId}${RESET_USER_MFA_SUFFIX}`,
  });
  return response;
};

// The signed in user's roles in every customer they belong to. No longer claims on the ID token
const getUserAccess = async (): Promise<UserAccessTypes> => {
  const response = await api({
    method: 'get',
    url: `${ACCOUNTS_USERS_URL_START}/me`,
  });
  return response.data;
};

export const AuthenticationService = {
  login,
  changePassword,
  forgotPassword,
  replacePassword,
  resetPassword,
  refreshAccessToken,
  setProfile,
  submitOtpChallenge,
  loginTotpChallenge,
  loginSmsChallenge,
  setupTotpMfa,
  verifySoftwareToken,
  setupSmsMfa,
  verifyPhoneNumber,
  updateSecurityPolicy,
  resetUserMfa,
  getUserAccess,
};
